<template>
  <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
    <div
      v-for="offering in offeringsToShow"
      @click="purchaseFlowStore.selected_offering = offering"
      class="flex gap-5 h-[85px] md:h-[110px] items-center px-5 py-4 bg-container-bg col-span-1 rounded-xl cursor-pointer border-2"
      :class="
        purchaseFlowStore?.selected_offering?.record_id === offering.record_id
          ? 'border-radio-checkbox-outline'
          : 'border-transparent'
      "
    >
      <div
        class="rounded-full h-6 w-6 border flex items-center justify-center"
        :class="
          purchaseFlowStore?.selected_offering?.record_id === offering.record_id
            ? 'bg-radio-checkbox-checked border-transparent'
            : 'border-radio-checkbox-outline'
        "
      >
        <Icon
          v-if="purchaseFlowStore?.selected_offering?.record_id === offering.record_id"
          name="Check"
          class="text-radio-checkbox-outline h-4 w-4"
          :stroke-width="2.5"
        />
      </div>
      <div class="font-medium flex-1">
        <p class="font-inter text-lg text-strongest">{{ offering.interest_rate }}% interest rate</p>
        <p class="font-inter text-strong">
          {{
            offering.is_short_term
              ? `${offering.bond_term}-month`
              : offering.bond_term >= 12
                ? offering.bond_term / 12 + "-year"
                : offering.bond_term + "-month"
          }}
          term
        </p>
      </div>
      <div class="text-right">
        <p
          class="font-inter text-lg font-medium"
          :class="isHighestRate(offering) ? 'text-success font-semibold' : 'text-strongest'"
        >
          {{ formatCurrency(anticipatedEarnings(offering)) }}
        </p>
        <p class="font-inter text-sm text-strong font-medium">anticipated returns</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { Icon } from "@/components";
import { usePurchaseFlowStore, useDataStore } from "@/stores";
import { totalInterest } from "@/utilities";

const purchaseFlowStore = usePurchaseFlowStore();
const dataStore = useDataStore();

const principal = computed(() => parseAmount(purchaseFlowStore.current_investment.amount));
const isCompounded = computed(() => purchaseFlowStore.current_investment.is_compounded);

function parseAmount(value) {
  if (typeof value === "number") return value;
  return Number(String(value).replace(/[^0-9.-]/g, "")) || 0;
}

function anticipatedEarnings(offering) {
  return totalInterest(principal.value, offering.interest_rate, offering.bond_term, {
    compounded: isCompounded.value,
    isShortTerm: offering.is_short_term,
  });
}

function isHighestRate(offering) {
  if (!offeringsToShow.value?.length) return false;
  return offering.interest_rate === offeringsToShow.value[0].interest_rate;
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

const offeringsToShow = computed(() => {
  if (purchaseFlowStore.selected_offering.type === "Reg D") {
    if (purchaseFlowStore.selected_offering.is_short_term) {
      return dataStore.offerings
        .filter(
          (offering) =>
            offering.type === "Reg D" &&
            offering.is_short_term === purchaseFlowStore.selected_offering.is_short_term
        )
        .sort((a, b) => b.interest_rate - a.interest_rate);
    } else {
      return dataStore.offerings
        .filter(
          (offering) =>
            offering.type === "Reg D" &&
            offering.tier === purchaseFlowStore.regDTier &&
            offering.is_short_term === purchaseFlowStore.selected_offering.is_short_term
        )
        .sort((a, b) => b.interest_rate - a.interest_rate);
    }
  }

  if (purchaseFlowStore.selected_offering.type === "S1") {
    return dataStore.offerings
      .filter(
        (offering) =>
          offering.type === "S1" &&
          offering.is_short_term === purchaseFlowStore.selected_offering.is_short_term
      )
      .sort((a, b) => b.interest_rate - a.interest_rate);
  }
});

defineExpose({
  async validate() {
    return { proceed: true };
  },
  afterContinue: async () => await purchaseFlowStore.saveInvestmentProgress(),
});
</script>
